// Lucka g9: säkerställ att nyckeldokument når alla på ett språk de förstår.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DocumentHeader from "./DocumentHeader";
import { Languages, Plus, X, CheckCircle2 } from "lucide-react";

interface Props {
  projectId: string;
  canEdit?: boolean;
}

interface Delivery {
  id: string;
  language: string;
  doc_number: string;
  delivered: boolean;
  method: string | null;
  delivered_by_name: string | null;
  delivered_at: string | null;
}

// Dokument som alla på arbetsplatsen måste förstå.
const KEY_DOCS: { doc: string; title: string }[] = [
  { doc: "12", title: "Arbetsmiljöplan – sammanfattning" },
  { doc: "15", title: "Skydds- och ordningsregler" },
  { doc: "16", title: "Arbetsberedningar" },
  { doc: "18", title: "Säkerhetsintroduktion" },
  { doc: "28", title: "Rutin för tillbud och olyckor" },
];

const SUGGESTED = ["Engelska", "Polska", "Arabiska", "Ukrainska", "Rumänska"];

const LanguageDeliveries: React.FC<Props> = ({ projectId, canEdit = true }) => {
  const [languages, setLanguages] = useState<string[]>([]);
  const [rows, setRows] = useState<Delivery[]>([]);
  const [newLang, setNewLang] = useState("");
  const [signer, setSigner] = useState("");

  const load = useCallback(async () => {
    const [p, d] = await Promise.all([
      supabase.from("projects").select("required_languages").eq("id", projectId).maybeSingle(),
      supabase.from("language_deliveries").select("*").eq("project_id", projectId),
    ]);
    setLanguages(((p.data?.required_languages as string[]) || []).filter(Boolean));
    setRows((d.data || []) as Delivery[]);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveLanguages = async (next: string[]) => {
    const { error } = await supabase
      .from("projects")
      .update({ required_languages: next })
      .eq("id", projectId);
    if (error) {
      toast.error("Kunde inte spara språken");
      return;
    }
    setLanguages(next);
  };

  const addLanguage = (lang: string) => {
    const v = lang.trim();
    if (!v || languages.includes(v)) return;
    saveLanguages([...languages, v]);
    setNewLang("");
  };

  const find = (lang: string, doc: string) =>
    rows.find((r) => r.language === lang && r.doc_number === doc);

  const toggle = async (lang: string, doc: string) => {
    const existing = find(lang, doc);
    const delivered = !existing?.delivered;
    const { error } = await supabase.from("language_deliveries").upsert(
      {
        project_id: projectId,
        language: lang,
        doc_number: doc,
        delivered,
        delivered_at: delivered ? new Date().toISOString() : null,
        delivered_by_name: delivered ? signer.trim() || null : null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "project_id,language,doc_number" }
    );
    if (error) {
      toast.error("Kunde inte spara");
      return;
    }
    load();
  };

  const total = languages.length * KEY_DOCS.length;
  const done = rows.filter((r) => r.delivered && languages.includes(r.language)).length;

  return (
    <div className="space-y-4">
      <DocumentHeader
        icon={Languages}
        title="Språk på arbetsplatsen"
        description="Alla som arbetar på platsen ska förstå planen och reglerna. Ange vilka språk som behövs och kryssa av när underlaget har delats på det språket."
        legalRef="AFS 2023:3"
        meta={
          <Badge variant={total && done === total ? "secondary" : "outline"}>
            {done}/{total || 0} delat
          </Badge>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Språk som behövs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {languages.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Inga extra språk angivna – allt förmedlas på svenska.
              </p>
            )}
            {languages.map((l) => (
              <Badge key={l} variant="secondary" className="gap-1">
                {l}
                {canEdit && (
                  <button
                    type="button"
                    aria-label={`Ta bort ${l}`}
                    onClick={() => saveLanguages(languages.filter((x) => x !== l))}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
          {canEdit && (
            <>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED.filter((s) => !languages.includes(s)).map((s) => (
                  <Button key={s} size="sm" variant="outline" onClick={() => addLanguage(s)}>
                    <Plus className="mr-1 h-3 w-3" /> {s}
                  </Button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newLang}
                  onChange={(e) => setNewLang(e.target.value)}
                  placeholder="Annat språk"
                />
                <Button onClick={() => addLanguage(newLang)}>Lägg till</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {languages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Vad som delats per språk</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {canEdit && (
              <div className="max-w-xs space-y-1">
                <Label>Ditt namn (sparas på kvittensen)</Label>
                <Input value={signer} onChange={(e) => setSigner(e.target.value)} />
              </div>
            )}
            {languages.map((lang) => (
              <div key={lang} className="rounded-md border p-3">
                <p className="mb-2 font-medium">{lang}</p>
                <div className="space-y-1">
                  {KEY_DOCS.map((d) => {
                    const row = find(lang, d.doc);
                    return (
                      <div
                        key={d.doc}
                        className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 p-2 text-sm"
                      >
                        <Badge variant="outline" className="text-[10px]">
                          Dok {d.doc}
                        </Badge>
                        <span className="min-w-0 flex-1 truncate">{d.title}</span>
                        {row?.delivered ? (
                          <Badge variant="secondary" className="gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Delat
                            {row.delivered_by_name ? ` – ${row.delivered_by_name}` : ""}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">Ej delat</span>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="ghost" onClick={() => toggle(lang, d.doc)}>
                            {row?.delivered ? "Ångra" : "Markera delat"}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LanguageDeliveries;
